import React, { useState } from 'react';
import DatasetDialog, { neutralClass } from 'features/datasets/components/DatasetDialog';
import { restoreDatasetVersion } from 'features/datasets/api/datasetsApi';
import { useAppDialog } from 'shared/context/AppDialogContext';

const REASON = {
  manual: 'Saved by hand', save: 'Saved', edit: 'Edited', add: 'Rows added', update: 'Update', live: 'Live source',
  experiment: 'Experiment', feedback: 'Feedback', restore: 'Restore', lucid: 'Lucid',
};

const when = (d) => {
  try { return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; }
};

/**
 * Every version of the dataset, newest first: what made it and how many rows
 * it holds. Restore makes an older one the dataset's rows again (that is a
 * new version too, so nothing is lost).
 */
export default function VersionsDialog({ datasetId, versions = [], canWrite = false, onClose, onRestored }) {
  const { confirm } = useAppDialog();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const latest = versions.length ? versions[0].version_no : null;

  const restore = async (v) => {
    const ok = await confirm(`The dataset's rows become version ${v.version_no}'s (${v.item_count} rows). This is saved as a new version.`, { title: `Restore v${v.version_no}`, confirmLabel: 'Restore' });
    if (!ok) return;
    setBusy(v.version_no); setError(null);
    try {
      const out = await restoreDatasetVersion(datasetId, v.version_no);
      onRestored?.(out);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally { setBusy(null); }
  };

  return (
    <DatasetDialog title="Versions" onClose={onClose} width="max-w-[640px]" footer={<button type="button" onClick={onClose} className={neutralClass}>Close</button>}>
      {versions.length ? (
        <ul className="flex flex-col divide-y divide-[color:var(--border-subtle)]" aria-label="Versions">
          {versions.map((v) => (
            <li key={v.version_no} className="flex items-center gap-3 py-2 text-[12px]">
              <span className="w-10 shrink-0 font-mono text-[color:var(--text-muted)]">v{v.version_no}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-[color:var(--text-primary)]">{v.name || `v${v.version_no}`}</span>
                <span className="block truncate text-[11px] text-[color:var(--text-muted)]">
                  {[REASON[v.reason] || null, `${Number(v.item_count) || 0} rows`, when(v.created_at), v.message || null].filter(Boolean).join(' · ')}
                </span>
              </span>
              {v.version_no === latest ? (
                <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">Current</span>
              ) : canWrite ? (
                <button type="button" onClick={() => restore(v)} disabled={busy != null} className={`${neutralClass} !h-7 shrink-0`}>
                  {busy === v.version_no ? 'Restoring…' : 'Restore'}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : <p className="text-[12px] text-[color:var(--text-muted)]">No versions yet. Every change to the rows saves one.</p>}
      {error ? <p className="text-[12px] text-danger">{error}</p> : null}
    </DatasetDialog>
  );
}
